// КАРТОЧКА ДОГОВОРА (смарт-процесс 153, «Реестр договоров») — в том же живом виде, что
// карточки сделки и компании: каркас components/card (CardShell, полоса стадий,
// FieldSection/FieldRow, Timeline), слева вкладки, справа лента.
//
// ЧЕМ ОНА ОТЛИЧАЕТСЯ ОТ ТЕХ КАРТОЧЕК — И ПОЧЕМУ ЭТО НЕ «ПОКА НЕ УСПЕЛИ».
//   • Всё зеркальное и только чтение: реестр наполняют 10 бизнес-процессов Bitrix,
//     запись из CRM их перезапустила бы (та же причина, что у раздела /smart/contracts).
//   • Раскладки «как в Bitrix» здесь нет: у типа 153 нет снятого макета (entityLayouts
//     знает компанию/контакта/судно). Вместо неё разделы построены ПО ЗАПОЛНЕННОСТИ из
//     справочника /fields (bx_fields, entity='item:153'): что заполняют всегда — сверху,
//     что пусто во всём реестре — свёрнуто именами. Это честнее выдуманного порядка.
//   • Комментариев и своих полей нет и у них написана причина: лента карточек ядра
//     (card_feed/cards_fields) ведётся только у сделки, компании, контакта и судна —
//     сущности «договор» там нет. Лента справа собрана из вех зеркала и заданий движка.
//
// ДВА НОМЕРА ОДНОГО ДОГОВОРА. Адрес карточки — номер записи Bitrix (im 153). Движок
// процессов помнит договор СВОИМ номером (таблица contract своей схемы), поэтому карточка
// сначала находит след переноса (/api/docs/item?src=own), а уже его номером спрашивает
// задания (/api/bpe/tasks?entity=договор&doc_id=…). Нет следа — нет и заданий, и карточка
// говорит это словами, а не пустой таблицей. Подробности — в api/contractCard.ts.
//
// СВЯЗЬ СО СДЕЛКАМИ живёт в СДЕЛКЕ: поле-привязка UF_CRM_1621413940 «Реестр договоров»
// (jsonb-массив номеров записей 153). Список на вкладке «Сделки» — обратный фильтр по
// этому полю; сверх него показывается сделка-родитель самой записи (parentId2), если её
// в списке нет: родителя проставляет бизнес-процесс, а поле в сделке заполняют люди,
// и эти два множества расходятся.
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  фильтрСделокДоговора, useContractTasks, useOwnContract,
} from '../api/contractCard'
import type { ЗаданиеДвижка } from '../api/contractCard'
import { файлыBitrix } from '../api/entityLayouts'
import { useDeals, useFields, useItem, useRefs } from '../api/hooks'
import type { FieldsApi } from '../api/hooks'
import type { CrmItem, FieldMeta } from '../api/types'
import { CardShell } from '../components/card/CardShell'
import type { CardTab } from '../components/card/CardShell'
import type { CardField } from '../components/card/FieldRow'
import { FieldSection } from '../components/card/FieldSection'
import { StageBar } from '../components/card/StageBar'
import { Timeline } from '../components/card/Timeline'
import type { TimelineEvent } from '../components/card/Timeline'
import { ПРОЧЕРК } from '../components/cardEntities'
import { EntityLink } from '../components/EntityLink'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, plural } from '../lib/format'
import { fmtMoneyField } from '../lib/money'
import type { RefsApi } from '../lib/refs'
import { bitrixItemUrl, parseCrmRef, refId, smartEntity } from '../lib/smart'

/** Тип смарт-процесса «Реестр договоров». */
export const ТИП_ДОГОВОРА = 153

/** Сколько связанных записей показываем без «показать все» — как в карточке компании. */
const RELATED = 10

// ─────────────────── что знаем о конкретных полях типа 153 ───────────────────
// Коды сняты с боевого /fields?entity=item:153 (31.07.2026). Это НЕ раскладка полей —
// только особые случаи показа, которые из типа поля не выводятся.

/** «Все сделки по договору» — строковое multiple-поле, но значения в нём номера сделок. */
const ПОЛЕ_ВСЕ_СДЕЛКИ = 'ufCrm1_1677674009385'

/**
 * Поля-ссылки на ДРУГОЙ ДОГОВОР («Основной договор», «Доп соглашение»). Тип у них crm,
 * а значения — номер записи 153: голый («2856») или с префиксом динамического типа
 * («T99_3855»; 0x99 = 153). Проверено запросом к crm_items боевого зеркала 31.07.2026.
 */
const ПОЛЯ_ДРУГОГО_ДОГОВОРА = new Set(['ufCrm1_1702977797', 'ufCrm1_1702977822'])

/** «T99_3855» или «2856» → номер договора; ноль и не-номер — null. */
const номерДоговора = (v: unknown): string | null => {
  const m = /^(?:T99_)?(\d+)$/.exec(String(v ?? '').trim())
  return m && m[1] !== '0' ? m[1] : null
}

/** Границы групп заполненности: ≥90 «почти всегда», ≥30 «часто», >0 «редко». */
const ГРАНИЦА_ВСЕГДА = 90
const ГРАНИЦА_ЧАСТО = 30

// ─────────────────────────── карточка ───────────────────────────

type Вкладка = 'main' | 'deals' | 'tasks'

export default function ContractCard() {
  const { id } = useParams<{ id: string }>()
  /**
   * «own-15» — запись, заведённая уже в своей схеме: зеркального двойника у неё нет,
   * и спрашивать crm.item.get значило бы показать «не найдено» там, где верный ответ
   * «в Bitrix её никогда не было» (тот же случай, что в SmartItemsTable).
   */
  const изBitrix = !!id && /^\d+$/.test(id)
  const q = useItem(ТИП_ДОГОВОРА, изBitrix ? id : undefined)
  const f = useFields(smartEntity(ТИП_ДОГОВОРА))
  const refs = useRefs()

  const own = useOwnContract(id)
  const след = own.data
  const tasks = useContractTasks(след?.вид === 'есть' ? след.doc.own_id : null)

  // Сделки, привязанные к договору полем «Реестр договоров». Для own-записи фильтр
  // заведомо пуст (номера Bitrix у неё нет) — экран объяснит это словами.
  const deals = useDeals({
    filter: фильтрСделокДоговора(id ?? ''),
    order: { DATE_MODIFY: 'DESC' },
    select: ['ID', 'TITLE', 'STAGE_ID', 'CATEGORY_ID', 'OPPORTUNITY', 'CURRENCY_ID', 'DATE_MODIFY'],
  })

  const [вкладка, setВкладка] = useState<Вкладка>('main')

  const item = q.data
  const заданий = tasks.data?.вид === 'есть' ? tasks.data.total : undefined

  const вкладки: CardTab[] = [
    { key: 'main', label: 'Общие' },
    { key: 'deals', label: deals.data && !deals.data.stale ? `Сделки · ${deals.data.total}` : 'Сделки' },
    { key: 'tasks', label: заданий !== undefined ? `Задания движка · ${заданий}` : 'Задания движка' },
  ]

  // Полоса стадий — из справочника ядра, как в карточке сделки. Без справочника полосы
  // нет вовсе (StageBar сам не рисует пустую рамку).
  const стадии = useMemo(
    () => refs.itemStageOptions(ТИП_ДОГОВОРА, String(item?.categoryId ?? ''))
      .map((o) => ({ id: o.id, name: o.name, kind: refs.itemStageKind(o.id) })),
    [refs, item?.categoryId],
  )

  const события = useСобытияЛенты(item, след, tasks.data?.вид === 'есть' ? tasks.data.rows : [], refs)

  const заголовок = item?.title
    ? fmtText(item.title)
    : след?.вид === 'есть' && след.doc.doc_number
      ? `Договор № ${след.doc.doc_number}`
      : `Договор #${id}`

  return (
    <>
      <div className="page-head">
        <h1>{заголовок}</h1>
        <div className="spacer" />
        <Link to="/smart/contracts">← к списку</Link>
      </div>

      {/* Записи из своей схемы зеркальный запрос не делается вовсе — карточка живёт
          на следе own. Для зеркальной записи ошибки/загрузку ведёт QueryState. */}
      {!изBitrix ? (
        <КарточкаБезЗеркала id={id} след={след} ждём={own.isLoading}
                            ошибка={own.error} tasks={tasks} события={события} />
      ) : (
        <QueryState isLoading={q.isLoading} error={q.error}>
          {item && (
            <CardShell
              head={
                <div className="cshell-head">
                  <div className="cshell-title">{заголовок}</div>
                  <div className="spacer" />
                  <span className="muted">
                    Компания:{' '}
                    <EntityLink kind="company" id={refId(item.companyId)} resolve
                                empty="не привязана" />
                  </span>
                  <span className="muted">
                    Ответственный: {refs.userName(refId(item.assignedById))}
                  </span>
                  <a href={bitrixItemUrl(ТИП_ДОГОВОРА, String(item.id))} target="_blank"
                     rel="noreferrer" title="Открыть эту же запись в Bitrix">
                    в Bitrix ↗
                  </a>
                </div>
              }
              stageBar={<StageBar stages={стадии} current={String(item.stageId ?? '')} />}
              tabs={вкладки}
              active={вкладка}
              onTab={(k) => setВкладка(k as Вкладка)}
              left={
                <>
                  {вкладка === 'main' && (
                    <Поля item={item} f={f} refs={refs} след={след}
                          ждёмСлед={own.isLoading} ошибкаСледа={own.error} />
                  )}
                  {вкладка === 'deals' && <Сделки item={item} deals={deals} refs={refs} />}
                  {вкладка === 'tasks' && (
                    <Задания tasks={tasks} след={след} ждёмСлед={own.isLoading} />
                  )}
                </>
              }
              right={
                <>
                  <h2>Лента</h2>
                  <Timeline
                    events={события}
                    note={'Комментарии и свои поля для договоров ядро ещё не ведёт: лента '
                      + 'карточек знает только сделку, компанию, контакт и судно. Здесь — '
                      + 'вехи зеркала Bitrix и задания движка процессов.'}
                    emptyText="Событий в ленте нет"
                  />
                </>
              }
            />
          )}
        </QueryState>
      )}
    </>
  )
}

// ═══════════════════ запись, заведённая уже в своей схеме ═══════════════════

function КарточкаБезЗеркала({ id, след, ждём, ошибка, tasks, события }: {
  id: string | undefined
  след: ReturnType<typeof useOwnContract>['data']
  ждём: boolean
  ошибка: unknown
  tasks: ReturnType<typeof useContractTasks>
  события: TimelineEvent[]
}) {
  return (
    <QueryState isLoading={ждём} error={ошибка}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hint">
          Запись {fmtText(id)} заведена уже в своей схеме — в Bitrix её никогда не было,
          зеркальных полей у неё нет. Показано то, что есть в своей схеме; связанные сделки
          по полю Bitrix для такой записи не найти.
        </div>
      </div>
      <СвояСхема след={след} ждём={ждём} ошибка={undefined} />
      <div className="card" style={{ marginTop: 16 }}>
        <h2>Задания движка</h2>
        <ТаблицаЗаданий tasks={tasks} след={след} ждёмСлед={ждём} />
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        <h2>Лента</h2>
        <Timeline events={события} emptyText="Событий в ленте нет" />
      </div>
    </QueryState>
  )
}

// ═══════════════════ вкладка «Общие»: поля по заполненности ═══════════════════

/**
 * Значение поля типа 153 → готовый вид. Правила те же, что в раскрытой строке списка
 * (lib/smart.cellText), плюс ссылки: компания/контакт/сделка/другой договор — ссылками,
 * файлы — счётом (сам файл лежит в Bitrix и из CRM не открывается).
 */
function видЗначения(code: string, meta: FieldMeta | undefined, v: unknown,
                     f: FieldsApi, refs: RefsApi): ReactNode {
  const тип = (meta?.type ?? '').toLowerCase()
  const массив = Array.isArray(v) ? v : [v]
  const построчно = (список: ReactNode[]): ReactNode =>
    список.map((x, i) => <div key={i}>{x}</div>)

  if (code === ПОЛЕ_ВСЕ_СДЕЛКИ) {
    return построчно(массив.map((x) => <EntityLink kind="deal" id={refId(x)} />))
  }
  if (ПОЛЯ_ДРУГОГО_ДОГОВОРА.has(code)) {
    return построчно(массив.map((x) => {
      const н = номерДоговора(x)
      return н
        ? <Link to={`/smart/contracts/${н}`}>договор #{н}</Link>
        : <span className="mono">{fmtText(x)}</span>
    }))
  }

  switch (тип) {
    case 'file': {
      const файлы = файлыBitrix(v)
      if (файлы.length === 0) return ПРОЧЕРК
      return (
        <span title="Файл хранится в Bitrix — из CRM он не открывается">
          {файлы.length === 1
            ? 'файл'
            : `${файлы.length} ${plural(файлы.length, 'файл', 'файла', 'файлов')}`}
          <span className="hint" style={{ marginLeft: 6 }}>в Bitrix</span>
        </span>
      )
    }
    case 'money':
      return построчно(массив.map((x) => fmtMoneyField(x)))
    case 'enumeration':
      return массив.map((x) => fmtText(f.enumText(code, x))).join(', ')
    case 'crm_status':
      // у типа 153 это стадии (previousStageId и техполя с кодом стадии)
      return массив.map((x) => refs.itemStageName(String(x ?? ''))).join(', ')
    case 'employee':
    case 'user':
      return построчно(массив.map((x) => refs.userName(refId(x))))
    case 'crm':
    case 'crm_entity': {
      return построчно(массив.map((x) => {
        const ссылка = parseCrmRef(x)
        return ссылка
          ? <EntityLink kind={ссылка.kind} id={ссылка.id} resolve />
          : <span className="mono">{fmtText(x)}</span>
      }))
    }
    case 'date':
      return массив.map((x) => fmtDate(String(x ?? ''))).join(', ')
    case 'datetime':
      return массив.map((x) => fmtDate(String(x ?? ''), true)).join(', ')
    case 'boolean':
    case 'char':
      return v === true || v === 'Y' || v === '1' || v === 1 ? 'да' : 'нет'
    default:
      return Array.isArray(v) ? построчно(массив.map((x) => fmtText(x))) : fmtText(v)
  }
}

/** Пустота по-битриксовски: '', null, пустой массив, а у привязок ещё и '0'. */
function пусто(v: unknown, тип?: string): boolean {
  if (v === null || v === undefined || v === '') return true
  if (Array.isArray(v) && v.length === 0) return true
  const т = (тип ?? '').toLowerCase()
  if ((т === 'crm' || т === 'crm_entity' || т === 'employee' || т === 'user')
      && refId(Array.isArray(v) ? v[0] : v) === '') return true
  if ((т === 'boolean' || т === 'char') && (v === 'N' || v === false || v === '0')) return true
  return false
}

function строкаПоля(code: string, meta: FieldMeta | undefined, item: CrmItem,
                    f: FieldsApi, refs: RefsApi): CardField {
  const v = item[code]
  const п = пусто(v, meta?.type)
  return {
    code,
    label: f.titleOr(code, code),
    type: 'text',
    value: п ? null : (v as unknown),
    origin: 'bitrix',
    readOnly: true,
    display: п ? ПРОЧЕРК : видЗначения(code, meta, v, f, refs),
    hint: `${code}${meta?.bp_computed ? ' · значение считает бизнес-процесс Bitrix' : ''}`,
  }
}

function Поля({ item, f, refs, след, ждёмСлед, ошибкаСледа }: {
  item: CrmItem
  f: FieldsApi
  refs: RefsApi
  след: ReturnType<typeof useOwnContract>['data']
  ждёмСлед: boolean
  ошибкаСледа: unknown
}) {
  // UF-поля типа из справочника, разложенные по заполненности РЕЕСТРА (не этой записи):
  // порядок отвечает на вопрос «куда смотреть», а прочерк в «почти всегда» — сам по себе
  // сигнал. Поле с fill=0, но заполненное У ЭТОЙ записи, поднимается в «редко»:
  // статистика считается ночами и отстаёт от живой записи.
  const группы = useMemo(() => {
    const всегда: CardField[] = []
    const часто: CardField[] = []
    const редко: CardField[] = []
    const пустые: Array<{ code: string; label: string }> = []
    const статистикаЕсть = f.list.some(({ meta }) => meta.fill !== undefined)

    for (const { code, meta } of f.list) {
      if (!code.startsWith('ufCrm')) continue   // системные поля — в «Связи» и «Служебные»
      const строка = строкаПоля(code, meta, item, f, refs)
      const fill = meta.fill
      if (!статистикаЕсть) { часто.push(строка); continue }
      if ((fill ?? 0) === 0 && строка.value === null) {
        пустые.push({ code, label: строка.label })
      } else if ((fill ?? 0) >= ГРАНИЦА_ВСЕГДА) всегда.push(строка)
      else if ((fill ?? 0) >= ГРАНИЦА_ЧАСТО) часто.push(строка)
      else редко.push(строка)
    }
    const поЗаполненности = (a: CardField, b: CardField) =>
      (f.meta(b.code)?.fill ?? 0) - (f.meta(a.code)?.fill ?? 0)
    всегда.sort(поЗаполненности)
    часто.sort(поЗаполненности)
    редко.sort(поЗаполненности)
    return { всегда, часто, редко, пустые, статистикаЕсть }
  }, [f, item, refs])

  // Связи записи — системные поля, у которых есть куда вести.
  const связи: CardField[] = [
    { code: 'companyId', label: f.titleOr('companyId', 'Компания'), type: 'text',
      origin: 'bitrix', readOnly: true, value: refId(item.companyId) || null,
      display: <EntityLink kind="company" id={refId(item.companyId)} resolve /> },
    { code: 'contactId', label: f.titleOr('contactId', 'Контакт'), type: 'text',
      origin: 'bitrix', readOnly: true, value: refId(item.contactId) || null,
      display: <EntityLink kind="contact" id={refId(item.contactId)} resolve /> },
    { code: 'parentId2', label: f.titleOr('parentId2', 'Сделка'), type: 'text',
      origin: 'bitrix', readOnly: true, value: refId(item.parentId2) || null,
      display: <EntityLink kind="deal" id={refId(item.parentId2)} resolve /> },
    { code: 'assignedById', label: f.titleOr('assignedById', 'Ответственный'), type: 'text',
      origin: 'bitrix', readOnly: true, value: refId(item.assignedById) || null,
      display: refs.userName(refId(item.assignedById)) },
  ]

  const служебные: CardField[] = [
    { code: 'id', label: 'ID записи', type: 'text', origin: 'bitrix', readOnly: true,
      value: item.id, display: <span className="mono">{String(item.id)}</span> },
    { code: 'stageId', label: f.titleOr('stageId', 'Стадия'), type: 'text',
      origin: 'bitrix', readOnly: true, value: item.stageId ?? null,
      display: (
        <span className={`badge ${refs.itemStageKind(String(item.stageId ?? ''))}`}>
          {refs.itemStageName(String(item.stageId ?? ''))}
        </span>
      ) },
    { code: 'createdTime', label: f.titleOr('createdTime', 'Когда создан'), type: 'text',
      origin: 'bitrix', readOnly: true, value: item.createdTime ?? null,
      display: fmtDate(String(item.createdTime ?? ''), true) },
    { code: 'updatedTime', label: f.titleOr('updatedTime', 'Когда обновлён'), type: 'text',
      origin: 'bitrix', readOnly: true, value: item.updatedTime ?? null,
      display: fmtDate(String(item.updatedTime ?? ''), true) },
    { code: 'movedTime', label: f.titleOr('movedTime', 'Дата изменения стадии'), type: 'text',
      origin: 'bitrix', readOnly: true, value: item.movedTime ?? null,
      display: fmtDate(String(item.movedTime ?? ''), true) },
  ]

  return (
    <div className="card">
      {!f.loaded && (
        <div className="hint" style={{ marginBottom: 10 }}>
          Справочник полей смарт-процесса 153 из ядра не пришёл: названия полей показаны
          кодами Bitrix, групп по заполненности нет — их не из чего собрать.
        </div>
      )}
      {f.loaded && f.stale && (
        <div className="hint" style={{ marginBottom: 10 }}>
          Справочник полей давно не обновлялся из Bitrix — названия и заполненность могут
          отставать.
        </div>
      )}

      <FieldSection title="Связи" fields={связи} />
      {группы.всегда.length > 0 && (
        <FieldSection title="Заполняются почти всегда" fields={группы.всегда} />
      )}
      {группы.часто.length > 0 && (
        <FieldSection
          title={группы.статистикаЕсть ? 'Заполняются часто' : 'Поля договора'}
          fields={группы.часто}
        />
      )}
      {группы.редко.length > 0 && (
        <FieldSection title="Заполняются редко" fields={группы.редко} />
      )}
      <FieldSection title="Служебные" fields={служебные} />

      {группы.пустые.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary className="hint" style={{ cursor: 'pointer' }}>
            Поля, пустые во всём реестре: {группы.пустые.length}
          </summary>
          <div className="hint" style={{ marginTop: 8 }}>
            {группы.пустые.map((п) => п.label).join(' · ')}
          </div>
        </details>
      )}

      <div style={{ marginTop: 16 }}>
        <СвояСхема след={след} ждём={ждёмСлед} ошибка={ошибкаСледа} />
      </div>

      <div className="hint" style={{ marginTop: 12 }}>
        Карточка только читает, и это не «пока не успели»: записи реестра создают и двигают
        бизнес-процессы Bitrix (на снимке 28.07.2026 — 10 шаблонов), запись из CRM их
        перезапустила бы.
      </div>
    </div>
  )
}

// ═══════════════════ след своей схемы ═══════════════════

/** Подписи колонок own-карточки — тех, что docs_api отдаёт добавками. */
const ПОДПИСИ_OWN: Record<string, string> = {
  kind_id: 'Вид договора',
  parent_id: 'Основной договор (своя схема)',
  valid_from: 'Действует с',
  released_at: 'Выпущен',
  cancelled_at: 'Аннулирован',
  cancel_reason: 'Причина аннулирования',
  rev: 'Ревизия',
  bitrix_source: 'Источник переноса',
}

function СвояСхема({ след, ждём, ошибка }: {
  след: ReturnType<typeof useOwnContract>['data']
  ждём: boolean
  ошибка: unknown
}) {
  if (ждём) return <div className="skeleton" style={{ width: '60%' }} />
  if (ошибка) {
    return (
      <div className="hint">
        След своей схемы не прочитался: {(ошибка as Error).message}. Задания движка без
        него не найти — у движка договор записан своим номером, а не номером Bitrix.
      </div>
    )
  }
  if (!след) return null
  if (след.вид === 'нет') {
    return (
      <section className="fieldsec">
        <div className="fieldsec-title">Своя схема</div>
        <div className="hint">{след.почему}</div>
      </section>
    )
  }

  const d = след.doc
  const поля: CardField[] = [
    { code: 'own_id', label: 'Свой номер (ключ движка процессов)', type: 'text',
      readOnly: true, value: d.own_id, display: <span className="mono">{d.own_id}</span> },
    { code: 'doc_state', label: 'Состояние', type: 'text', readOnly: true,
      value: d.state_name ?? d.doc_state,
      display: d.state_name ? <span className="badge">{d.state_name}</span> : ПРОЧЕРК },
    { code: 'doc_number', label: 'Номер договора', type: 'text', readOnly: true,
      value: d.doc_number, display: d.doc_number ? fmtText(d.doc_number) : ПРОЧЕРК },
    { code: 'own_company', label: 'Наше юрлицо', type: 'text', readOnly: true,
      value: d.own_company_title,
      display: d.own_company_title ? fmtText(d.own_company_title) : ПРОЧЕРК },
    ...след.поля.map((п): CardField => ({
      code: `own-${п.code}`,
      label: ПОДПИСИ_OWN[п.code] ?? п.code,
      type: 'text',
      readOnly: true,
      value: п.value as unknown,
      display: /_at$|_from$/.test(п.code)
        ? fmtDate(String(п.value ?? ''), /_at$/.test(п.code))
        : fmtText(п.value),
    })),
  ]

  return (
    <FieldSection title="Своя схема" fields={поля}>
      <div className="hint">
        Это след переноса в свою базу (таблица contract). Заданиям движка процессов договор
        известен именно этим номером.
      </div>
    </FieldSection>
  )
}

// ═══════════════════ вкладка «Сделки» ═══════════════════

function Сделки({ item, deals, refs }: {
  item: CrmItem
  deals: ReturnType<typeof useDeals>
  refs: RefsApi
}) {
  const rows = deals.data?.items ?? []
  const родитель = refId(item.parentId2)
  const родительВСписке = rows.some((d) => String(d.ID) === родитель)

  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Сделки по договору</h2>
        <div className="spacer" />
        <span className="muted">
          {deals.data && !deals.data.stale ? `всего: ${deals.data.total}` : ''}
        </span>
      </div>
      <div className="hint" style={{ marginBottom: 10 }}>
        Связь ищется по полю сделки «Реестр договоров» (UF_CRM_1621413940): здесь те сделки,
        в которых указан этот договор. Сделка-родитель самой записи показана отдельно,
        если в поле её не вписали, — эти два списка ведут разные руки и расходятся.
      </div>
      <QueryState isLoading={deals.isLoading} error={deals.error}
                  stale={deals.data?.stale} ageMin={deals.data?.ageMin}
                  scope={deals.data?.scope}>
        {родитель && !родительВСписке && (
          <div style={{ marginBottom: 10 }}>
            Сделка-родитель записи:{' '}
            <EntityLink kind="deal" id={родитель} resolve />
          </div>
        )}
        {rows.length === 0 ? (
          <div className="empty">
            <div className="empty-title">
              Сделок с этим договором в поле «Реестр договоров» нет
            </div>
          </div>
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Название</th>
                  <th className="nowrap">Стадия</th>
                  <th className="nowrap">Изменена</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, RELATED).map((d) => (
                  <tr key={d.ID}>
                    <td className="mono nowrap" data-label="ID">
                      <EntityLink kind="deal" id={d.ID} title={d.ID} />
                    </td>
                    <td data-label="Название">
                      <EntityLink kind="deal" id={d.ID} title={fmtText(d.TITLE)} />
                    </td>
                    <td className="nowrap" data-label="Стадия">
                      <span className={`badge ${refs.stageKind(d.STAGE_ID)}`}>
                        {refs.stageName(d.STAGE_ID)}
                      </span>
                    </td>
                    <td className="nowrap" data-label="Изменена">
                      {fmtDate(d.DATE_MODIFY)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(deals.data?.total ?? 0) > RELATED && (
          <div className="hint" style={{ marginTop: 8 }}>
            Показаны первые {RELATED} из {deals.data?.total} по свежести изменения.
          </div>
        )}
      </QueryState>
    </div>
  )
}

// ═══════════════════ вкладка «Задания движка» ═══════════════════

function Задания({ tasks, след, ждёмСлед }: {
  tasks: ReturnType<typeof useContractTasks>
  след: ReturnType<typeof useOwnContract>['data']
  ждёмСлед: boolean
}) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Задания движка процессов</h2>
        <div className="spacer" />
        <Link to="/bpe-tasks">весь раздел →</Link>
      </div>
      <ТаблицаЗаданий tasks={tasks} след={след} ждёмСлед={ждёмСлед} />
    </div>
  )
}

function ТаблицаЗаданий({ tasks, след, ждёмСлед }: {
  tasks: ReturnType<typeof useContractTasks>
  след: ReturnType<typeof useOwnContract>['data']
  ждёмСлед: boolean
}) {
  if (ждёмСлед || (след?.вид === 'есть' && tasks.isLoading)) {
    return <div className="skeleton" style={{ width: '70%' }} />
  }
  // Без следа своей схемы заданий не найти: движок помнит договор своим номером.
  if (!след || след.вид === 'нет') {
    return (
      <div className="hint">
        Задания движка ключуются СВОИМ номером договора (таблица contract), а следа своей
        схемы у этой записи нет{след?.вид === 'нет' ? `: ${след.почему}` : ''}.
      </div>
    )
  }
  if (tasks.error) {
    return <div className="error">Задания не прочитались: {(tasks.error as Error).message}</div>
  }
  const данные = tasks.data
  if (!данные) return null
  if (данные.вид === 'нет') return <div className="hint">{данные.почему}</div>
  if (данные.rows.length === 0) {
    return (
      <div className="empty">
        <div className="empty-title">
          {данные.engine_started
            ? 'Заданий по этому договору движок не выдавал'
            : 'Движок процессов ещё не запускали — выдавать задания было некому'}
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Задание</th>
            <th className="nowrap">Состояние</th>
            <th className="nowrap">Срок</th>
            <th>Кому</th>
            <th>Процесс</th>
          </tr>
        </thead>
        <tbody>
          {данные.rows.map((т) => (
            <tr key={т.id}>
              <td data-label="Задание">
                {fmtText(т.name)}
                {т.body && <div className="hint">{fmtText(т.body)}</div>}
              </td>
              <td className="nowrap" data-label="Состояние">
                {т.closed_at
                  ? (
                    <span className="badge won" title={fmtDate(т.closed_at, true)}>
                      закрыто{т.outcome ? `: ${fmtText(т.outcome)}` : ''}
                    </span>
                  )
                  : <span className="badge work">открыто</span>}
              </td>
              <td className="nowrap" data-label="Срок">
                {т.due_at
                  ? (
                    <span className={т.due_state === 'просрочено' ? 'error' : undefined}>
                      {fmtDate(т.due_at)}
                    </span>
                  )
                  : <span className="muted">без срока</span>}
              </td>
              <td data-label="Кому">
                {(т.assignees_info ?? []).map((и) => и.name).filter(Boolean).join(', ')
                  || <span className="muted">—</span>}
              </td>
              <td data-label="Процесс">
                {fmtText(т.instance_caption || т.process)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════ лента ═══════════════════

/**
 * События ленты: вехи зеркала (заведён, смена стадии) + своя схема (выпущен) + задания
 * движка (выдано/закрыто). Комментариев нет — причина написана на самой ленте.
 */
function useСобытияЛенты(
  item: CrmItem | undefined,
  след: ReturnType<typeof useOwnContract>['data'],
  задания: ЗаданиеДвижка[],
  refs: RefsApi,
): TimelineEvent[] {
  return useMemo(() => {
    const из: TimelineEvent[] = []
    if (item?.createdTime) {
      из.push({
        id: 'created', at: String(item.createdTime), kind: 'bitrix', origin: 'bitrix',
        text: 'Договор заведён в реестре',
      })
    }
    if (item?.movedTime && item.stageId) {
      const прежняя = refId(item.previousStageId)
      из.push({
        id: 'stage', at: String(item.movedTime), kind: 'stage', origin: 'bitrix',
        text: прежняя
          ? `Стадия: ${refs.itemStageName(прежняя)} → ${refs.itemStageName(String(item.stageId))}`
          : `Стадия: ${refs.itemStageName(String(item.stageId))}`,
      })
    }
    if (след?.вид === 'есть') {
      const выпуск = след.поля.find((п) => п.code === 'released_at')?.value
      if (выпуск) {
        из.push({
          id: 'own-released', at: String(выпуск), kind: 'stage', origin: 'crm',
          text: `Выпущен в своей схеме${след.doc.state_name ? ` (${след.doc.state_name})` : ''}`,
        })
      }
    }
    for (const т of задания) {
      if (т.created_at) {
        из.push({
          id: `bpe-${т.id}`, at: т.created_at, kind: 'задание', origin: 'crm',
          text: `Движок выдал задание: ${т.name}`,
        })
      }
      if (т.closed_at) {
        из.push({
          id: `bpe-${т.id}-closed`, at: т.closed_at, kind: 'задание', origin: 'crm',
          text: `Задание закрыто${т.outcome ? ` (${т.outcome})` : ''}: ${т.name}`,
        })
      }
    }
    return из
  }, [item, след, задания, refs])
}
